import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { User } from "../types/user";
import { can, type Permission } from "./roles";
import { api, getToken, setToken, clearToken } from "../api/client";

type AuthState =
  | { kind: "loading" }
  | { kind: "unauthenticated" }
  | { kind: "authenticated"; user: User };

// Account-setup payload (invite-gated registration) — see
// docs/auth-beta-contract.md §3.
export type RegisterArgs = {
  inviteToken: string;
  name: string;
  password: string;
  accountType: "organization" | "individual";
  orgName?: string | null;
};

type AuthContextValue = {
  state: AuthState;
  user: User | null;
  can: (permission: Permission) => boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (args: RegisterArgs) => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  logout: () => void;
};

const Ctx = createContext<AuthContextValue | null>(null);

// The user payload shared by /login, /register and /me. The multi-org fields
// are optional until the backend ships them (contract §4).
type UserResponse = {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl: string | null;
  createdAt: string;
  lastActiveAt: string;
  activeOrg?: User["activeOrg"];
  memberships?: User["memberships"];
  isStaff?: boolean;
};

function toUser(u: UserResponse): User {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    role: u.role as User["role"],
    avatarUrl: u.avatarUrl,
    createdAt: u.createdAt,
    lastActiveAt: u.lastActiveAt,
    activeOrg: u.activeOrg ?? null,
    memberships: u.memberships ?? [],
    isStaff: u.isStaff ?? false,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: "loading" });

  const user = state.kind === "authenticated" ? state.user : null;

  // On mount, check for existing token
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setState({ kind: "unauthenticated" });
      return;
    }
    api.get<UserResponse>("/api/auth/me")
      .then((u) => {
        setState({ kind: "authenticated", user: toUser(u) });
      })
      .catch(() => {
        clearToken();
        setState({ kind: "unauthenticated" });
      });
  }, []);

  const canDo = useCallback(
    (permission: Permission) => (user ? can(user.role, permission) : false),
    [user],
  );

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ token: string; user: UserResponse }>("/api/auth/login", { email, password });
    setToken(res.token);
    setState({ kind: "authenticated", user: toUser(res.user) });
  }, []);

  const register = useCallback(async (args: RegisterArgs) => {
    // Invite-gated. Backend creates the user + org (or personal org) and returns
    // the same { token, user } shape as login, already scoped to the new org.
    const res = await api.post<{ token: string; user: UserResponse }>("/api/auth/register", args);
    setToken(res.token);
    setState({ kind: "authenticated", user: toUser(res.user) });
  }, []);

  const switchOrg = useCallback(async (orgId: string) => {
    // Re-mint the token scoped to the target org, then hard-reload into the app
    // so every org-scoped cache (projects, policies, settings…) refetches under
    // the new active org rather than showing stale cross-org data.
    const res = await api.post<{ token: string }>("/api/auth/switch-org", { orgId });
    setToken(res.token);
    window.location.assign("/app");
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setState({ kind: "unauthenticated" });
  }, []);

  return (
    <Ctx.Provider value={{ state, user, can: canDo, login, register, switchOrg, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
